import PDFDocument from 'pdfkit/js/pdfkit.standalone.js';
import { cmToPoint, renderFooter, renderHeader } from './helpers/index';
import { COLOR_BLACK, COLOR_GRAY, COLOR_RED, COLOR_WHITE, FONT, FONT_BOLD, FONT_SIZE_LARGE, FONT_SIZE_NORMAL, SPACE } from './variables';

export async function createRimAppraisalPdf(body: any, afterInit: (pdf: PDFDocument) => Promise<void>) {
	// create a document and pipe to a blob
	const pdf = new PDFDocument({
		bufferPages: true,
		layout: 'portrait',
		size: 'A4',
		margins: {
			top: cmToPoint(1.27),
			bottom: cmToPoint(1.27),
			left: cmToPoint(2),
			right: cmToPoint(2)
		}
	});

	await afterInit(pdf);

	await renderHeader(pdf, body);
	renderApproval(pdf, body);
	renderDetails(pdf, body);
	renderCarType(pdf, body);
	renderTexts(pdf, body);
	renderFooter(pdf, body);

	return pdf;
}

function renderApproval(pdf: PDFDocument, body: any) {
	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;

	pdf.fontSize(FONT_SIZE_LARGE).font(FONT_BOLD);

	const texts = body.approvalTexts;

	const padding = cmToPoint(SPACE);
	let height = padding;
	for (const text of texts) {
		height += pdf.heightOfString(text);
	}

	pdf.rect(pdf.x, pdf.y, width, height).fill(COLOR_RED);

	pdf.y += padding / 2;

	for (const text of texts) {
		pdf.fillColor(COLOR_WHITE).text(text, {
			align: 'center'
		});
	}

	pdf.y += cmToPoint(SPACE * 2) + padding / 2;
}

function renderDetails(pdf: PDFDocument, body: any) {
	if (!body.details?.length) {
		return;
	}

	const hasMixedAxles = body.details.some((d: any) => !!d.value2);

	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;
	// For single axle: value starts at 2/3 of width. For mixed: label takes 55%, two value columns share the rest.
	const col2Start = hasMixedAxles ? width * 0.55 : width / 2 + width / 6;
	const col3Start = width * 0.775; // only used when hasMixedAxles

	const resetX = pdf.x;
	const margin = 5;

	const labelColWidth = col2Start - margin * 2;
	const valueColWidth = hasMixedAxles ? col3Start - col2Start : width - col2Start;
	const value2ColWidth = width - col3Start;

	const textOptions = {
		lineBreak: false,
		ellipsis: true
	};

	// for text height calculation
	pdf.fontSize(FONT_SIZE_NORMAL).font(FONT_BOLD);

	const padding = cmToPoint(SPACE);
	let height = padding;
	for (let i = 0; i < body.details.length; i++) {
		const text = body.details[i].label;

		const maxHeight = Math.max(
			pdf.heightOfString(text, { ...textOptions, width: labelColWidth }),
			pdf.heightOfString(body.details[i].value ?? '', { ...textOptions, width: valueColWidth }),
			pdf.heightOfString(body.details[i].value2 ?? '', { ...textOptions, width: value2ColWidth })
		);

		height += maxHeight + (i === 0 ? 0 : margin);
	}

	pdf.rect(pdf.x, pdf.y, width, height).fill(COLOR_GRAY);

	pdf.y += padding / 2;

	for (let i = 0; i < body.details.length; i++) {
		const detail = body.details[i];

		pdf.y += i === 0 ? 0 : margin;

		const resetY = pdf.y;

		pdf.x = resetX + margin * 2;
		pdf.fontSize(FONT_SIZE_NORMAL)
			.font(FONT)
			.fillColor(COLOR_BLACK)
			.text(detail.label, { ...textOptions, width: labelColWidth });

		pdf.x = col2Start;
		pdf.y = resetY;
		pdf.fontSize(FONT_SIZE_NORMAL)
			.font(FONT_BOLD)
			.fillColor(COLOR_BLACK)
			.text(detail.value ?? '', { ...textOptions, width: valueColWidth });

		if (hasMixedAxles && !!detail.value2) {
			pdf.x = col3Start;
			pdf.y = resetY;
			pdf.fontSize(FONT_SIZE_NORMAL)
				.font(FONT_BOLD)
				.fillColor(COLOR_BLACK)
				.text(detail.value2, { ...textOptions, width: value2ColWidth });
		}
	}

	pdf.x = resetX;
	pdf.y += cmToPoint(SPACE * 2) + padding / 2;
}

function renderCarType(pdf: PDFDocument, body: any) {
	const texts = [
		'Eignung für Fahrzeugmarke und Fahrzeugtyp:',
		'Adéquation à la marque et au type de véhicule:',
		'Idoneità per la marca e il tipo di veicolo:'
	];

	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;
	const columnWidth = width / 2 + width / 6;

	const resetX = pdf.x;
	const margin = 5;

	// for text height calculation
	pdf.fontSize(FONT_SIZE_NORMAL).font(FONT_BOLD);

	const padding = cmToPoint(SPACE);
	let height = padding;
	for (let i = 0; i < texts.length; i++) {
		height += pdf.heightOfString(texts[i]) + (i === 0 ? 0 : margin);
	}

	pdf.rect(pdf.x, pdf.y, width, height).fill(COLOR_GRAY);

	pdf.y += padding / 2;

	let resetY = pdf.y;
	let startY = 0;

	for (let i = 0; i < texts.length; i++) {
		const text = texts[i];

		pdf.y += i === 0 ? 0 : margin;

		if (i === 1) {
			startY = pdf.y;
		}

		pdf.x = resetX + margin * 2;
		pdf.fontSize(FONT_SIZE_NORMAL).font(FONT).fillColor(COLOR_BLACK).text(text);

		resetY = pdf.y;
	}

	if (body.car) {
		pdf.x = columnWidth;
		pdf.y = startY;
		pdf.fontSize(FONT_SIZE_NORMAL).font(FONT_BOLD).fillColor(COLOR_BLACK).text(body.car);
	}

	pdf.x = resetX;
	pdf.y = resetY;
	pdf.y += cmToPoint(SPACE * 2) + padding / 2;
}

function renderTexts(pdf: PDFDocument, body: any) {
	pdf.fontSize(FONT_SIZE_NORMAL).fillColor(COLOR_BLACK);

	for (let i = 0; i < body.texts.length; i++) {
		const item = body.texts[i];

		if (i > 0 && i + 1 <= body.texts.length) {
			pdf.y += cmToPoint(SPACE);
		}

		if (item.bold) {
			pdf.font(FONT_BOLD);
		} else {
			pdf.font(FONT);
		}

		for (const text of item.texts) {
			pdf.text(text);
			pdf.y += cmToPoint(SPACE);
		}
	}
}
